import { useState } from "react";
import { Lock, ShieldCheck, RotateCcw } from "lucide-react";
import { PhotoPicker } from "@/components/PhotoPicker";
import { AuthedImage } from "@/components/AuthedImage";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDateTime } from "@/lib/api";

const PhotoGrid = ({ photos, onDelete }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="final-photos-grid">
    {photos.map((p) => (
      <AuthedImage key={p.id} fileId={p.id} className="aspect-square" testId={`final-photo-${p.id}`}
        caption={`Foto por ${p.uploaded_by?.name} em ${fmtDateTime(p.uploaded_at)}`}
        onDelete={onDelete ? () => onDelete(p.id) : null} />
    ))}
  </div>
);

const Sealed = ({ vehicle, isAdmin, onReopen }) => (
  <section className="rounded-xl border border-[#00E676]/50 bg-[#00E676]/[0.06] p-5 space-y-4" data-testid="finalized-seal">
    <div className="flex items-start gap-3">
      <ShieldCheck className="w-8 h-8 text-[#00E676] shrink-0" />
      <div>
        <div className="font-display text-xl font-black uppercase tracking-wide text-[#00E676]">Carregamento finalizado</div>
        <p className="text-sm text-slate-300" data-testid="finalized-by-text">
          por <strong className="text-white">{vehicle.finalized_by?.name}</strong> ({vehicle.finalized_by?.email})
        </p>
        <p className="font-mono text-xs text-slate-400" data-testid="finalized-at-text">{fmtDateTime(vehicle.finalized_at)}</p>
        {vehicle.pending_at_finalize > 0 && (
          <p className="text-xs text-[#FFB300] mt-1" data-testid="finalized-pending-text">{vehicle.pending_at_finalize} item(ns) não conferido(s) no fechamento</p>
        )}
      </div>
    </div>
    {vehicle.notes && <p className="text-sm text-slate-300 whitespace-pre-wrap border-l-2 border-[#2A2E39] pl-3" data-testid="finalized-notes">{vehicle.notes}</p>}
    <PhotoGrid photos={vehicle.final_photos} />
    {isAdmin && (
      <Button variant="outline" onClick={onReopen} className="h-12 border-[#2A2E39] bg-transparent text-slate-300 hover:bg-[#1C2029] hover:text-white" data-testid="reopen-vehicle-btn">
        <RotateCcw className="w-4 h-4 mr-2" /> Reabrir checklist
      </Button>
    )}
  </section>
);

export const FinalizePanel = ({ vehicle, pending, isAdmin, onPhoto, onDeletePhoto, onFinalize, onReopen }) => {
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  if (vehicle.finalized_at) return <Sealed vehicle={vehicle} isAdmin={isAdmin} onReopen={onReopen} />;
  const hasPhoto = vehicle.final_photos.length > 0;

  return (
    <section className="rounded-xl border border-[#636b2f]/60 bg-[#15181E] p-5 space-y-4 glow-cyan" data-testid="finalize-panel">
      <div>
        <div className="font-display text-xl font-black uppercase tracking-wide text-white flex items-center gap-2"><Lock className="w-5 h-5 text-[#A9B26A]" /> Fechamento</div>
        <p className="text-sm text-slate-400">Tire a foto do caminhão carregado antes de finalizar.</p>
      </div>
      {hasPhoto && <PhotoGrid photos={vehicle.final_photos} onDelete={onDeletePhoto} />}
      <PhotoPicker onFile={onPhoto} label={hasPhoto ? "Adicionar outra foto" : "Foto final do caminhão"} testId="final-photo-btn" className="w-full h-12" />
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observações (opcional)" className="bg-[#0B0C10] border-[#2A2E39] min-h-[80px]" data-testid="finalize-notes-input" />
      <Button disabled={!hasPhoto} onClick={() => setConfirm(true)} className="h-14 w-full bg-[#636b2f] text-white hover:bg-[#737c37] font-display text-lg font-black uppercase tracking-wider disabled:opacity-40" data-testid="finalize-vehicle-btn">
        Finalizar {vehicle.name}
      </Button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#15181E] border-[#2A2E39] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase">Finalizar {vehicle.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {pending > 0 ? `Ainda há ${pending} item(ns) não conferido(s). ` : "Todos os itens foram conferidos. "}
              Seu nome ficará registrado como responsável e o checklist será travado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2A2E39] text-slate-300 hover:bg-[#1C2029] hover:text-white" data-testid="finalize-cancel-btn">Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => onFinalize(notes)} className="bg-[#636b2f] text-white hover:bg-[#737c37] font-bold" data-testid="finalize-confirm-btn">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
